import { Box, Divider, Typography } from "@mui/material";
import { BlobReader, BlobWriter, ZipReader } from "@zip.js/zip.js";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CloudUploadSVG from "../../../../../assets/TailoredGeneration/icons/cloud_upload.svg";
import SuccessIcon from "../../../../../assets/common/icons/success-icon.svg";
import { useAdvancedUploadValidation } from "../../../../../components/TailoredGeneration/SharedPanels/TgAdvancedImageUploadSetup/hooks/useAdvancedUploadValidation.ts";
import BriaButton from "../../../../../components/common/BriaButton/BriaButton.tsx";
import BriaInput from "../../../../../components/common/BriaInput/BriaInput.tsx";
import BriaRadioButton from "../../../../../components/common/BriaRadioButton/BriaRadioButton.tsx";
import LoadingPlaceholder from "../../../../../components/common/LoadingPlaceholder/LoadingPlaceholder.tsx";
import ZipFileUploader from "../../../../../components/common/ZipFileUploader/ZipFileUploader.tsx";
import { useAppStore } from "../../../../../hooks/useStores.tsx";
import InputLayout from "../../../../../layout/InputLayout/InputLayout.tsx";
import TgDatasetTrainingGuide from "../components/TgDatasetTrainingGuide/TgDatasetTrainingGuide.tsx";
import styles from "./NewTgAdvancedUpload.module.scss";

type Props = {
	onSwitchToBasicDataset: () => void;
	onSetVisualSchema: () => void;
};

type UploadMethod = "zip" | "url";
const MAX_ZIP_SIZE_GB = 5;

const NewTgAdvancedUpload = ({ onSwitchToBasicDataset, onSetVisualSchema }: Props) => {
	const { t } = useTranslation("tg", { keyPrefix: "models.create.advancedUpload" });
	const { tgStore, uiStore } = useAppStore();
	const { tgProjectStore, tgDatasetStore } = tgStore;
	const [isCreatingDataset, setIsCreatingDataset] = useState(true);
	const [isUploadingDataset, setIsUploadingDataset] = useState(false);

	const [zipUrl, setZipUrl] = useState("");
	const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
	const [zipUploadProgress, setZipUploadProgress] = useState(0);
	const [sourceZipFile, setSourceZipFile] = useState<File | null>(null);

	const [isUploadingZipFile, setIsUploadingZipFile] = useState(false);
	const [isUploadingZipUrl, setIsUploadingZipUrl] = useState(false);
	const [uploadMethod, setUploadMethod] = useState<UploadMethod>("zip");
	const [urlUploadError, setUrlUploadError] = useState<string | null>(null);
	const [urlUploadSuccess, setUrlUploadSuccess] = useState(false);

	const { validateZipBlob, getValidationErrorMessage, countZipImages } = useAdvancedUploadValidation();

	useEffect(() => {
		const createInitialDataset = async () => {
			try {
				setIsCreatingDataset(true);
				const payload = {
					...tgDatasetStore.datasetForm,
					name: "temp",
					upload_type: "advanced" as const,
					project_id: tgProjectStore.projectToView.id,
					training_version: "fibo",
				};

				tgDatasetStore.datasetForm = await tgDatasetStore.createDataset(payload as any);
			} catch (e: any) {
				if (e?.response?.data?.message) {
					uiStore.showErrorAlertWithCode(e?.response?.status, e?.response?.data?.message);
				}
			} finally {
				setIsCreatingDataset(false);
			}
		};

		if (tgProjectStore.projectToView.id && !tgDatasetStore.datasetForm.id) {
			createInitialDataset();
		} else {
			setIsCreatingDataset(false);
		}
	}, []);

	const resetUrlState = () => {
		setZipUrl("");
		setUrlUploadError(null);
		setUrlUploadSuccess(false);
	};

	const startFileUpload = (file: File) => {
		setIsUploadingZipFile(true);
		setZipUploadProgress(0);
		setUploadedFiles([file]);
		setSourceZipFile(file);
		resetUrlState();
	};

	// Extract up to 10 images from the selected zip and return base64 strings (no data URL prefix)
	const extractSampleImagesFromZip = async (zipFile: File, maxImages: number = 10): Promise<string[]> => {
		try {
			const reader = new ZipReader(new BlobReader(zipFile));
			const entries = await reader.getEntries();
			const results: string[] = [];
			const isImage = (name: string) => {
				const lower = name.toLowerCase();
				return [".png", ".jpg", ".jpeg", ".webp"].some((ext) => lower.endsWith(ext));
			};
			const isValidImageEntry = (entry: any) =>
				!entry.directory && isImage(entry.filename) && !entry.filename.startsWith("__MACOSX/");
			for (const entry of entries) {
				if (results.length >= maxImages) break;
				if (!isValidImageEntry(entry) || typeof entry.getData !== "function") continue;

				const blob = await entry.getData(new BlobWriter());

				const base64 = await new Promise<string>((resolve, reject) => {
					const fr = new FileReader();
					fr.onload = () => {
						const res = fr.result as string;
						const commaIdx = res.indexOf(",");
						resolve(commaIdx >= 0 ? res.slice(commaIdx + 1) : res);
					};
					fr.onerror = reject;
					fr.readAsDataURL(blob);
				});

				results.push(base64);
			}

			await reader.close();
			return results;
		} catch (e) {
			console.error("Failed to extract sample images from zip", e);
			return [];
		}
	};

	const validateZipForFile = async (blob: Blob): Promise<boolean> => {
		const validation = await validateZipBlob(blob);
		if (!validation.ok) {
			const message = getValidationErrorMessage(validation);
			uiStore.showErrorAlertWithCode(400, message!);
			return false;
		}
		tgDatasetStore.advancedDatasetImages.total_count = await countZipImages(blob);
		return true;
	};

	const handleUploadDataSet = async () => {
		try {
			if (!tgDatasetStore.datasetForm.id) {
				// Incase create dataset failed
				const payload = {
					...tgDatasetStore.datasetForm,
					upload_type: "advanced" as const,
					project_id: tgProjectStore.projectToView.id,
					name: "temp",
					training_version: "fibo",
				};

				tgDatasetStore.datasetForm = await tgDatasetStore.createDataset(payload as any);
			}
			setIsUploadingDataset(true);
			if (sourceZipFile) {
				tgDatasetStore.advancedDatasetImages.sample_training_images = await extractSampleImagesFromZip(
					sourceZipFile,
					10,
				);
			}

			const payload = {
				...tgDatasetStore.datasetForm,
			};

			tgDatasetStore.datasetForm = await tgDatasetStore.updateDataset(
				tgDatasetStore.datasetForm.id,
				payload as any,
			);

			tgDatasetStore.advancedDatasetImages.sourceZipFile = tgDatasetStore.imagesZipUrl || undefined;
			onSetVisualSchema();
		} catch (e: any) {
			if (e?.response?.data?.message) {
				uiStore.showErrorAlertWithCode(e?.response?.status, e?.response?.data?.message);
			}
		} finally {
			setIsUploadingDataset(false);
		}
	};

	const resetFileState = () => {
		setUploadedFiles([]);
		setZipUploadProgress(0);
	};

	const handleUploadZip = async (zipFile: File) => {
		startFileUpload(zipFile);
		try {
			const valid = await validateZipForFile(zipFile);
			if (!valid) {
				resetFileState();
				return;
			}
			const zipName = `dataset_zip_images_${Date.now()}.zip`;
			await tgDatasetStore.uploadZipSimple(zipFile, (pct) => setZipUploadProgress(pct), zipName);
		} catch (e: any) {
			if (e?.response?.data?.message) {
				uiStore.showErrorAlertWithCode(e?.response?.status, e?.response?.data?.message);
			}
		} finally {
			setIsUploadingZipFile(false);
		}
	};

	const startUrlUpload = () => {
		setIsUploadingZipUrl(true);
		setUrlUploadError(null);
		setUrlUploadSuccess(false);
		resetFileState();
	};

	const validateZipForUrl = async (blob: Blob): Promise<boolean> => {
		const validation = await validateZipBlob(blob);
		if (!validation.ok) {
			const message = getValidationErrorMessage(validation);
			setUrlUploadError(message);
			setUrlUploadSuccess(false);
			return false;
		}
		tgDatasetStore.advancedDatasetImages.total_count = await countZipImages(blob);
		return true;
	};

	const handleUploadUrl = async () => {
		if (!zipUrl.trim()) {
			setUrlUploadError(t("urlRequired"));
			return;
		}

		startUrlUpload();

		try {
			const resp = await fetch(zipUrl, { method: "GET" });
			if (!resp.ok) throw new Error("fetch_failed");
			const blob = await resp.blob();
			const valid = await validateZipForUrl(blob);
			if (!valid) return;
			const fileName = `dataset_zip_images_${Date.now()}.zip`;
			const file = new File([blob], fileName, { type: blob.type || "application/zip" });
			await tgDatasetStore.uploadZipSimple(file);
			setSourceZipFile(file);
			setUrlUploadSuccess(true);
		} catch (error) {
			const isCors = error instanceof TypeError;
			setUrlUploadError(isCors ? t("errors.url.corsBlocked") : t("errors.url.fetchFailed"));
		} finally {
			setIsUploadingZipUrl(false);
		}
	};

	const handleRemoveFile = () => {
		setUploadedFiles([]);
		setZipUploadProgress(0);
		tgDatasetStore.imagesZipUrl = undefined;
		setSourceZipFile(null);
	};

	const handleRemoveUrl = () => {
		setZipUrl("");
		setUrlUploadSuccess(false);
		setUrlUploadError(null);
		tgDatasetStore.imagesZipUrl = undefined;
		setSourceZipFile(null);
	};

	return (
		<LoadingPlaceholder isLoading={isCreatingDataset} className={styles.loadingPlaceholder}>
			<Box className={styles.container}>
				<Box className={styles.left}>
					<Box className={styles.topSection}>
						{/* Image sources */}
						<InputLayout
							label={t("selectImageSource")}
							showLabel={true}
							labelSize={"title"}
							className={styles.imageSourcesContainer}
						>
							<BriaRadioButton
								buttons={[
									{
										label: t("uploadMethod.zip"),
										value: "zip",
										disabled: isUploadingZipFile || isUploadingZipUrl,
									},
									{
										label: t("uploadMethod.url"),
										value: "url",
										disabled: isUploadingZipFile || isUploadingZipUrl,
									},
								]}
								defaultValue={uploadMethod}
								onClick={(value) => {
									setUploadMethod(value as UploadMethod);
								}}
							/>

							{uploadMethod === "zip" ? (
								<Box className={styles.zipUploadSection}>
									<ZipFileUploader
										className={styles.zipUploaderWidget}
										onUpload={handleUploadZip}
										icon={<img src={CloudUploadSVG} className={styles.cloudIcon} />}
										handleRemoveFile={handleRemoveFile}
										maxSizeGB={MAX_ZIP_SIZE_GB}
										showUploadedFiles={true}
										uploadedFiles={uploadedFiles}
										showProgress={true}
										uploadProgress={zipUploadProgress}
										isUploading={isUploadingZipFile}
									/>
								</Box>
							) : (
								<Box className={styles.urlUploadContainer}>
									<Typography className={styles.urlSectionTItle}>{t("urlUploadTitle")}</Typography>
									<Box className={styles.urlUploadSection}>
										<Box className={styles.urlRow}>
											<BriaInput
												value={zipUrl}
												onChange={(e) => setZipUrl(e.target.value)}
												placeholder={t("urlPlaceholder")}
												className={styles.urlField}
												height={"40px"}
											/>
											<BriaButton
												buttonType={"secondaryMedium"}
												onClick={urlUploadSuccess ? handleRemoveUrl : handleUploadUrl}
												loading={isUploadingZipUrl}
												disabled={isUploadingZipUrl || !zipUrl.trim()}
												className={styles.uploadUrlButton}
											>
												{urlUploadSuccess ? t("remove") : t("upload")}
											</BriaButton>
										</Box>
										{urlUploadSuccess && (
											<Box className={styles.urlSuccessMessage}>
												<img alt={"success"} src={SuccessIcon} />
												<Box className={styles.successTextContainer}>
													<Typography className={styles.successTitle}>
														{t("urlUploadComplete")}
													</Typography>
													<Typography className={styles.successDescription}>
														{t("urlUploadDescription")}
													</Typography>
												</Box>
											</Box>
										)}
										{urlUploadError && (
											<Box className={styles.urlErrorMessage}>
												<Typography className={styles.errorIcon}>✕</Typography>
												<Box className={styles.errorTextContainer}>
													<Typography className={styles.errorTitle}>
														{t("urlUploadError")}
													</Typography>
													<Typography className={styles.errorDescription}>
														{urlUploadError}
													</Typography>
												</Box>
											</Box>
										)}
									</Box>
								</Box>
							)}
						</InputLayout>
					</Box>

					<Divider className={styles.sectionDivider} />
					{/* Buttons*/}
					<Box className={clsx(styles.uploadDatasetSection, styles.withSwitch)}>
						<BriaButton
							buttonType="textSmall"
							onClick={onSwitchToBasicDataset}
							className={styles.switchToBasicButton}
						>
							{t("switchToBasicUpload")}
						</BriaButton>

						<BriaButton
							buttonType="primarySmall"
							onClick={handleUploadDataSet}
							disabled={!tgDatasetStore.imagesZipUrl || isUploadingDataset}
							loading={isUploadingDataset}
							className={styles.uploadDatasetButton}
						>
							{t("setVisualSchema")}
						</BriaButton>
					</Box>
				</Box>

				<Box className={styles.right}>
					<TgDatasetTrainingGuide />
				</Box>
			</Box>
		</LoadingPlaceholder>
	);
};

export default observer(NewTgAdvancedUpload);
